import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";

import HomePage from "@/pages/HomePage";
import CrawlsPage from "@/pages/CrawlsPage";
import CrawlDetailPage from "@/pages/CrawlDetailPage";
import AboutPage from "@/pages/AboutPage";
import GroupsPage from "@/pages/GroupsPage";
import BlogPage from "@/pages/BlogPage";
import BlogPostPage from "@/pages/BlogPostPage";
import ContactPage from "@/pages/ContactPage";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";
import CheckoutSuccessPage from "@/pages/CheckoutSuccessPage";
import PrivacyPolicyPage from "@/pages/PrivacyPolicyPage";
import TermsPage from "@/pages/TermsPage";

import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const AuthContext = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('cbc_token'));

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (e) {
      logout();
    }
  };

  const login = (accessToken, userData) => {
    localStorage.setItem('cbc_token', accessToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
    setToken(accessToken);
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem('cbc_token');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
  };

  return { user, token, login, logout, isAdmin: user?.role === 'admin' };
};

const ProtectedRoute = ({ children, auth }) => {
  if (!auth.token || !auth.isAdmin) {
    return <Navigate to="/admin/login" replace />;
  }
  return children;
};

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

const AppLayout = ({ auth }) => {
  const location = useLocation();
  const isAdmin = location.pathname.startsWith('/admin');

  return (
    <>
      <ScrollToTop />
      {!isAdmin && <Navbar auth={auth} />}
      <main className={isAdmin ? '' : 'min-h-screen'}>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/crawls" element={<CrawlsPage />} />
          <Route path="/crawls/:id" element={<CrawlDetailPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/groups" element={<GroupsPage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:slug" element={<BlogPostPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/checkout/success" element={<CheckoutSuccessPage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="/terms-and-conditions" element={<TermsPage />} />
          <Route path="/admin/login" element={<AdminLogin auth={auth} />} />
          <Route path="/admin" element={
            <ProtectedRoute auth={auth}>
              <AdminDashboard auth={auth} />
            </ProtectedRoute>
          } />
        </Routes>
      </main>
      {!isAdmin && <Footer />}
      <Toaster position="top-right" richColors />
    </>
  );
};

function App() {
  const auth = AuthContext({});
  const [seedCalled, setSeedCalled] = useState(false);

  useEffect(() => {
    if (!seedCalled) {
      axios.post(`${API}/seed`).catch(() => {});
      setSeedCalled(true);
    }
  }, [seedCalled]);

  return (
    <div className="App">
      <BrowserRouter>
        <AppLayout auth={auth} />
      </BrowserRouter>
    </div>
  );
}

export default App;
